import { useMutation, useQueryClient } from '@tanstack/react-query';
import EventService, {
  IUpdateEventParams,
} from '@clickersphere/core/services/EventService';
import { LeadQueryData, LEADS_QUERY_KEY } from '../lead/useLeads';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { EventQueryData, EVENTS_QUERY_KEY } from './useEvents';

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: updateEventIsPending, mutateAsync: updateEventAsync } =
    useMutation({
      mutationFn: async ({
        id,
        data,
      }: {
        id: string;
        data: IUpdateEventParams;
      }) => EventService.update(id, { ...data }),
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(EVENTS_QUERY_KEY),
        });
        if (variables.data.leadId) {
          await queryClient.cancelQueries({
            queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.data.leadId]),
          });
        }

        const previousEvents = queryClient.getQueryData<EventQueryData[]>(
          buildQueryKey(EVENTS_QUERY_KEY),
        );
        const previousLead = variables.data.leadId
          ? queryClient.getQueryData<LeadQueryData>(
              buildQueryKey([LEADS_QUERY_KEY, variables.data.leadId]),
            )
          : undefined;

        queryClient.setQueryData<EventQueryData[]>(
          buildQueryKey(EVENTS_QUERY_KEY),
          (old) =>
            old?.map((event) =>
              event.id === variables.id
                ? { ...event, ...variables.data, queryStatus: 'pending' }
                : event,
            ),
        );

        if (previousLead && variables.data.leadId) {
          queryClient.setQueryData<LeadQueryData>(
            buildQueryKey([LEADS_QUERY_KEY, variables.data.leadId]),
            (old) => {
              if (!old) return old;
              return {
                ...old,
                events: (old.events ?? []).map((event) =>
                  event.id === variables.id
                    ? { ...event, ...variables.data, queryStatus: 'pending' }
                    : event,
                ),
              };
            },
          );
        }

        return { previousEvents, previousLead };
      },
      onSuccess: async (_data, variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(EVENTS_QUERY_KEY),
        });

        queryClient.setQueryData<EventQueryData[]>(
          buildQueryKey(EVENTS_QUERY_KEY),
          (old) =>
            old?.map((event) =>
              event.id === variables.id
                ? { ...event, queryStatus: undefined }
                : event,
            ),
        );

        if (variables.data.leadId) {
          await queryClient.cancelQueries({
            queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.data.leadId]),
          });

          queryClient.setQueryData<LeadQueryData>(
            buildQueryKey([LEADS_QUERY_KEY, variables.data.leadId]),
            (old) => {
              if (!old) return old;
              return {
                ...old,
                events: old.events?.map((event) =>
                  event.id === variables.id
                    ? { ...event, queryStatus: undefined }
                    : event,
                ),
              };
            },
          );
        }
      },
      onError: async (_error, variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(EVENTS_QUERY_KEY),
        });

        queryClient.setQueryData(
          buildQueryKey(EVENTS_QUERY_KEY),
          context?.previousEvents,
        );

        if (context?.previousLead && variables.data.leadId) {
          await queryClient.cancelQueries({
            queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.data.leadId]),
          });

          queryClient.setQueryData(
            buildQueryKey([LEADS_QUERY_KEY, variables.data.leadId]),
            context.previousLead,
          );
        }
      },
    });

  return {
    updateEventAsync,
    updateEventIsPending,
  };
}
